import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ReactNode, TextareaHTMLAttributes, useState } from 'react';

interface Props extends TextareaHTMLAttributes<HTMLInputElement|HTMLTextAreaElement> {
    showLength?: boolean;
    textAlign?: 'left' | 'center' | 'right';
    value?: string,
    setValue?: (value: string) => void;
    as?: 'input'|'textarea';
    min?: number;
    max?: number;
    trailing?: ReactNode;
    helperText?: string;
    errorText?: string;
}

function TextField(props: Props) {
    const {
        showLength, textAlign = 'left', setValue,
        value, maxLength, min, max,
        as: Component = 'input',
        onChange, trailing, helperText,
        className, errorText, ...rest
    } = props;
    const [length, setLength] = useState<number>(0);
    return (
        <Container>
            <Contents>
                <Component css={inputCss(textAlign, Component === 'input')}
                    className={className}
                    value={value}
                    maxLength={maxLength}
                    min={min}
                    max={max}
                    onChange={(e) => {
                        setValue?.(e.target.value);
                        setLength(e.target.value.length);
                        onChange?.(e);
                    }}
                    {...rest} />
                {showLength && (
                    <Count isTextarea={Component === 'textarea'}>
                        {length} /  {maxLength}
                    </Count>
                )}
                {trailing && (
                    <Trailing>{trailing}</Trailing>
                )}
            </Contents>
            {helperText && (
                <Text color={colors.gray6} size='s'>{helperText}</Text>
            )}
            {errorText && (
                <Text color={colors.red} size='s'>{errorText}</Text>
            )}
        </Container>
    );
}

const inputCss = (textAlign: 'left' | 'center' | 'right', isInput: boolean) => css`
    background-color: var(--color-gray1);
    border: 1px solid var(--color-gray4);
    border-radius: 6px;
    color: var(--color-gray9);
    font-family: PretendardFull, serif;
    font-size: 14px;
    line-height: 20px;
    padding: 10px 12px;
    resize: none;
    width: 100%;
    -webkit-user-select: text;
    caret-color: var(--color-gray9);
    text-align: ${textAlign};
    ${isInput && 'height: 40px;'}

    &::placeholder{
        color: var(--color-gray5);
        font-weight: 400;
    }

    &:focus {
        border: 1px solid var(--color-primary);
        outline: none;
    }

    &:read-only {
        background-color:  var(--color-gray1);
    }

    &:disabled {
        background-color: var(--color-gray4);
        border: 1px solid var(--color-gray3);
        color: var(--color-gray5);
    }
`;

const Count = styled.span<{
    isTextarea?: boolean;
}>`
    color: var(--color-gray8);
    font-size: 14px;
    position: absolute;
    right: 20px;
    bottom: ${({ isTextarea }) => (isTextarea ? '20px' : 'unset')};
`;

const Contents = styled.div`
    align-items: center;
    display: flex;
    position: relative;
    width: 100%;
`;

const Container = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    row-gap: 4px;
`;

const Trailing = styled.div`
    position: absolute;
    right: 0;
`;

export default TextField;
